import { ReactNode, useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import Text from '../Text';

interface IProps {
  title: string;
  children: ReactNode;
  /** 'top'이면 트리거 위에 표시(테이블 셀 등에서 가려지지 않음), 기본값 'bottom' */
  placement?: 'top' | 'bottom';
}

const TOOLTIP_OFFSET = 5;
const VIEWPORT_PAD = 8;

function getTooltipPosition(rect: DOMRect, placement: 'top' | 'bottom'): { top: number; left: number; transform: string } {
  const centerX = rect.left + rect.width / 2;
  if (placement === 'top') {
    return {
      top: rect.top - TOOLTIP_OFFSET,
      left: centerX,
      transform: 'translate(-50%, -100%)',
    };
  }
  return {
    top: rect.bottom + TOOLTIP_OFFSET,
    left: centerX,
    transform: 'translateX(-50%)',
  };
}

const TooltipTrigger = styled.span`
  display: inline-block;
  cursor: pointer;
`;

const TOOLTIP_BG = 'rgba(45, 48, 54, 0.97)';
const ARROW_SIZE = 6;

const TooltipWrap = styled.div<{
  $top: number;
  $left: number;
  $transform: string;
  $placement: 'top' | 'bottom';
}>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  transform: ${({ $transform }) => $transform};
  width: max-content;
  min-width: 0;
  max-width: min(280px, calc(100vw - ${VIEWPORT_PAD * 2}px));
  white-space: pre-wrap;
  word-break: break-word;
  z-index: 9999;
  background: ${TOOLTIP_BG};
  backdrop-filter: blur(8px);
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.15),
    0 2px 6px rgba(0, 0, 0, 0.08);
  pointer-events: none;
  animation: tooltipFadeIn 0.12s ease-out;

  @keyframes tooltipFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  ::before {
    content: '';
    width: 0;
    height: 0;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    border-right: ${ARROW_SIZE}px solid transparent;
    border-left: ${ARROW_SIZE}px solid transparent;
  }

  ${({ $placement }) =>
    $placement === 'top'
      ? `
    ::before {
      top: 100%;
      border-top: ${ARROW_SIZE}px solid ${TOOLTIP_BG};
      border-bottom: ${ARROW_SIZE}px solid transparent;
    }
  `
      : `
    ::before {
      bottom: 100%;
      border-top: ${ARROW_SIZE}px solid transparent;
      border-bottom: ${ARROW_SIZE}px solid ${TOOLTIP_BG};
    }
  `}
`;

function Tooltip({ title, children, placement = 'bottom' }: IProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    transform: string;
  } | null>(null);
  const [clampedOffset, setClampedOffset] = useState<{ deltaX: number; deltaY: number } | null>(null);

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition(getTooltipPosition(rect, placement));
      setClampedOffset(null);
    }
  }, [placement]);

  const handleMouseOver = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition(getTooltipPosition(rect, placement));
      setClampedOffset(null);
    }
    setActive(true);
  };

  const handleMouseOut = () => {
    setActive(false);
    setPosition(null);
    setClampedOffset(null);
  };

  useLayoutEffect(() => {
    if (!active || !position || !tooltipRef.current) return;
    const tr = tooltipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let deltaX = 0;
    let deltaY = 0;
    if (tr.left < VIEWPORT_PAD) deltaX = VIEWPORT_PAD - tr.left;
    if (tr.right > vw - VIEWPORT_PAD) deltaX = vw - VIEWPORT_PAD - tr.right;
    if (tr.top < VIEWPORT_PAD) deltaY = VIEWPORT_PAD - tr.top;
    if (tr.bottom > vh - VIEWPORT_PAD) deltaY = vh - VIEWPORT_PAD - tr.bottom;
    if (deltaX !== 0 || deltaY !== 0) {
      setClampedOffset({ deltaX, deltaY });
    }
  }, [active, position]);

  useEffect(() => {
    if (!active) return;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [active, updatePosition]);

  const appliedTop = position ? position.top + (clampedOffset?.deltaY ?? 0) : 0;
  const appliedLeft = position ? position.left + (clampedOffset?.deltaX ?? 0) : 0;

  const tooltipContent =
    active && position ? (
      <TooltipWrap ref={tooltipRef} $top={appliedTop} $left={appliedLeft} $transform={position.transform} $placement={placement}>
        <Text color="rgba(255,255,255,0.95)" fontSize="0.8125rem">
          {title}
        </Text>
      </TooltipWrap>
    ) : null;

  return (
    <>
      <TooltipTrigger ref={triggerRef} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
        {children}
      </TooltipTrigger>
      {tooltipContent && createPortal(tooltipContent, document.body)}
    </>
  );
}

export default Tooltip;
